import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { i18n } from 'i18n';
import PCNode from '../../MineModel/Nodes/PCNode';
import PCSpool from '../../MineModel/Spools/PCSpool';
import {
  AccessType,
  AssignmentResult,
  AutoAssignSolidInput,
  SearchBox,
  assignSolid,
  normalizeStopeName
} from './autoAssignUtils';
import { ExistingStopeBehavior } from './bulkImportSolidsSceneState';

interface WizardStepProcessingProps {
  solids: AutoAssignSolidInput[];
  nodes: PCNode[];
  spools: PCSpool[];
  accessType: AccessType;
  searchBox: SearchBox;
  existingStopesByName: Record<string, string>;
  existingStopeBehavior: ExistingStopeBehavior;
  onProgress: (progress: number) => void;
  onComplete: (results: AssignmentResult[]) => void;
}

const CHUNK_SIZE = 50;

const WizardStepProcessing = ({
  solids,
  nodes,
  spools,
  accessType,
  searchBox,
  existingStopesByName,
  existingStopeBehavior,
  onProgress,
  onComplete,
}: WizardStepProcessingProps) => {
  const [processed, setProcessed] = React.useState(0);
  const cancelledRef = React.useRef(false);

  const total = solids.length;
  const percent = total === 0 ? 100 : Math.round((processed / total) * 100);

  React.useEffect(() => {
    cancelledRef.current = false;
    const nonStopeNodes = nodes.filter((n) => !n.isStope);
    const reservedNodeIds = new Set<string>();
    const accumulated: AssignmentResult[] = [];

    const processChunk = (startIndex: number) => {
      if (cancelledRef.current) return;
      const endIndex = Math.min(startIndex + CHUNK_SIZE, solids.length);
      const chunk = solids.slice(startIndex, endIndex);

      chunk.forEach((solid) => {
        const existingStopeId = existingStopesByName[normalizeStopeName(solid.name)];
        if (existingStopeId) {
          if (existingStopeBehavior === 'skip') {
            accumulated.push({
              solidGuid: solid.guid,
              status: 'skipped_existing',
              reason: 'Stope name already exists',
            });
            return;
          }
          accumulated.push({
            solidGuid: solid.guid,
            status: 'update_existing',
            existingStopeId,
          });
          return;
        }
        const result = assignSolid(solid, {
          nodes: nonStopeNodes,
          spools,
          accessType,
          searchBox,
          reservedNodeIds,
        });
        if (result.status === 'mark_existing_node' && result.existingNodeId) {
          reservedNodeIds.add(result.existingNodeId);
        }
        accumulated.push(result);
      });

      setProcessed(endIndex);
      onProgress(endIndex);

      if (endIndex >= solids.length) {
        onComplete(accumulated);
        return;
      }
      setTimeout(() => processChunk(endIndex), 0);
    };

    if (solids.length === 0) {
      onComplete([]);
      return undefined;
    }

    setTimeout(() => processChunk(0), 0);

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
      <Typography variant="h6">{i18n._('Processing Solids')}</Typography>
      <CircularProgress variant="determinate" value={percent} size={72} color="secondary" />
      <Typography variant="body2" color="text.secondary">
        {i18n._('{processed} of {total} processed', { processed, total })}
      </Typography>
      <Box sx={{ width: '100%' }}>
        <LinearProgress variant="determinate" value={percent} color="secondary" />
      </Box>
    </Box>
  );
};

export default WizardStepProcessing;
